import * as React from "react";
import {
    List,
    Datagrid,
    TextField,
    Create,
    Edit,
    SimpleForm,
    ReferenceField,
    ReferenceInput,
    AutocompleteInput,
    TextInput,
    BooleanInput,
    NumberInput,
    required,
    FunctionField,
    CreateButton,
    FilterButton,
    TopToolbar,
    Show,
    SimpleShowLayout,
    ShowButton,
    EditButton,
    DateField,
    BooleanField,
    useRecordContext,
    SearchInput
} from 'react-admin';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import SportsIcon from '@mui/icons-material/Sports';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

import { createButtonCss, filterButtonCss, showButtonCss } from '../shared/Styles/buttonStyles';
import { listCss } from '../shared/Styles/listStyles';

const ListActions = () => (
    <TopToolbar>
        <CreateButton sx={createButtonCss} label="Associa Utente a Squadra" />
        <FilterButton sx={filterButtonCss} />
    </TopToolbar>
);

// Filtri
const UserTeamFilters = [
    <SearchInput source="q" placeholder="Cerca..." alwaysOn />,
    <ReferenceInput source="userId" reference="users" label="Utente">
        <AutocompleteInput optionText={(record) => `${record.nome} ${record.cognome} (${record.email})`} />
    </ReferenceInput>,
    <ReferenceInput source="teamId" reference="tbl-team" label="Squadra">
        <AutocompleteInput optionText="nome" />
    </ReferenceInput>,
];

// Lista migliorata
export const Tbl_user_teamList = props => (
    <List
        filters={UserTeamFilters}
        perPage={25}
        actions={<ListActions />}
        sort={{ field: 'id', order: 'DESC' }}
        {...props}
    >
        <Datagrid
            sx={{
                ...listCss,
                '& .RaDatagrid-headerCell': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    color: '#e85a2c'
                },
                '& .RaDatagrid-rowCell': {
                    padding: '12px',
                }
            }}
            bulkActionButtons={false}
        >
            {/* ID */}
            <TextField label="ID" source="id" sortable={true} />

            {/* Utente con nome e cognome */}
            <ReferenceField
                label="Utente"
                source="userId"
                reference="users"
                link="show"
                sortable={false}
            >
                <FunctionField
                    render={record => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                                sx={{
                                    bgcolor: '#2196f3',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.875rem'
                                }}
                            >
                                <PersonIcon fontSize="small" />
                            </Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {record?.nome || '-'} {record?.cognome || '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                    {record?.email}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                />
            </ReferenceField>

            {/* Squadra con nome */}
            <ReferenceField
                label="Squadra"
                source="teamId"
                reference="tbl-team"
                link="show"
                sortable={false}
            >
                <FunctionField
                    render={record => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                                src={record?.logo}
                                sx={{
                                    bgcolor: '#e85a2c',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.875rem'
                                }}
                            >
                                <SportsIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {record?.nome || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                />
            </ReferenceField>

            {/* Note */}
            <TextField label="Note" source="note" />

            {/* Stati con chip */}
            <FunctionField
                label="Stati"
                render={record => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {record.flAttivo ? (
                            <Tooltip title="Associazione attiva">
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Attivo"
                                    size="small"
                                    color="success"
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Associazione disattivata">
                                <Chip
                                    icon={<CancelIcon />}
                                    label="Disattivo"
                                    size="small"
                                    variant="outlined"
                                />
                            </Tooltip>
                        )}
                        
                        {record.flVisibile ? (
                            <Tooltip title="Visibile">
                                <Chip
                                    icon={<VisibilityIcon />}
                                    label="Visibile"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Nascosto">
                                <Chip
                                    icon={<VisibilityOffIcon />}
                                    label="Nascosto"
                                    size="small"
                                    variant="outlined"
                                    sx={{ opacity: 0.5 }}
                                />
                            </Tooltip>
                        )}

                        {record.flAnnullato ? (
                            <Tooltip title="Associazione annullata">
                                <Chip
                                    label="Annullato"
                                    size="small"
                                    color="error"
                                />
                            </Tooltip>
                        ) : null}
                    </Box>
                )}
            />

            {/* Data creazione */}
            <DateField
                label="Creato il"
                source="createdAt"
                showTime
                options={{
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                }}
            />

            {/* Azioni */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Visualizza dettagli">
                    <ShowButton sx={{ ...showButtonCss, minWidth: '32px', padding: '4px 8px' }} label="" />
                </Tooltip>
                <Tooltip title="Modifica">
                    <EditButton 
                        sx={{ 
                            ...showButtonCss, 
                            backgroundColor: '#ff9800',
                            minWidth: '32px',
                            padding: '4px 8px',
                            '&:hover': { backgroundColor: '#f57c00' }
                        }} 
                        label="" 
                    />
                </Tooltip>
            </Box>
        </Datagrid>
    </List>
);

// Form di creazione migliorato
export const Tbl_user_teamCreate = props => (
    <Create
        title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupAddIcon />
                <span>Associa Utente a Squadra</span>
            </Box>
        }
        {...props}
    >
        <SimpleForm>
            <Card sx={{ width: '100%', mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Selezione Utente e Squadra
                    </Typography>
                    
                    <ReferenceInput
                        source="userId"
                        reference="users"
                        label="Utente"
                        validate={required()}
                    >
                        <AutocompleteInput
                            optionText={(record) => 
                                record ? `${record.nome || ''} ${record.cognome || ''} (${record.email})` : ''
                            }
                            filterToQuery={searchText => ({ q: searchText })}
                            fullWidth
                        />
                    </ReferenceInput>

                    <ReferenceInput
                        source="teamId"
                        reference="tbl-team"
                        label="Squadra"
                        validate={required()}
                    >
                        <AutocompleteInput
                            optionText="nome"
                            filterToQuery={searchText => ({ q: searchText })}
                            fullWidth
                        />
                    </ReferenceInput>
                </CardContent>
            </Card>

            <Card sx={{ width: '100%', mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Configurazione
                    </Typography>

                    <TextInput source="note" label="Note" fullWidth multiline rows={3} />
                    <NumberInput source="ordinamento" label="Ordinamento" defaultValue={0} />

                    <Box sx={{ mt: 2 }}>
                        <BooleanInput source="flAttivo" label="Associazione attiva" defaultValue={true} />
                        <BooleanInput source="flVisibile" label="Visibile" defaultValue={true} />
                        <BooleanInput source="flAnnullato" label="Annullato" defaultValue={false} />
                    </Box>
                </CardContent>
            </Card>
        </SimpleForm>
    </Create>
);

// Form di modifica
export const Tbl_user_teamEdit = props => (
    <Edit
        title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupAddIcon />
                <span>Modifica Associazione Utente-Squadra</span>
            </Box>
        }
        {...props}
    >
        <SimpleForm>
            <Card sx={{ width: '100%', mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Selezione Utente e Squadra
                    </Typography>
                    
                    <ReferenceInput
                        source="userId"
                        reference="users"
                        label="Utente"
                        validate={required()}
                    >
                        <AutocompleteInput
                            optionText={(record) => 
                                record ? `${record.nome || ''} ${record.cognome || ''} (${record.email})` : ''
                            }
                            filterToQuery={searchText => ({ q: searchText })}
                            fullWidth
                        />
                    </ReferenceInput>

                    <ReferenceInput
                        source="teamId"
                        reference="tbl-team"
                        label="Squadra"
                        validate={required()}
                    >
                        <AutocompleteInput
                            optionText="nome"
                            filterToQuery={searchText => ({ q: searchText })}
                            fullWidth
                        />
                    </ReferenceInput>
                </CardContent>
            </Card>

            <Card sx={{ width: '100%', mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Configurazione
                    </Typography>

                    <TextInput source="note" label="Note" fullWidth multiline rows={3} />
                    <NumberInput source="ordinamento" label="Ordinamento" />

                    <Box sx={{ mt: 2 }}>
                        <BooleanInput source="flAttivo" label="Associazione attiva" />
                        <BooleanInput source="flVisibile" label="Visibile" />
                        <BooleanInput source="flAnnullato" label="Annullato" />
                    </Box>
                </CardContent>
            </Card>
        </SimpleForm>
    </Edit>
);

// Pagina dettaglio
export const Tbl_user_teamShow = props => (
    <Show
        title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupAddIcon />
                <span>Dettaglio Associazione Utente-Squadra</span>
            </Box>
        }
        {...props}
    >
        <SimpleShowLayout>
            <Card sx={{ width: '100%', mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Informazioni Associazione
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">ID</Typography>
                        <Typography variant="body1"><TextField source="id" /></Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">Utente</Typography>
                        <ReferenceField source="userId" reference="users" link="show">
                            <FunctionField
                                render={record => (
                                    <Typography variant="body1">
                                        {record?.nome} {record?.cognome} ({record?.email})
                                    </Typography>
                                )}
                            />
                        </ReferenceField>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">Squadra</Typography>
                        <ReferenceField source="teamId" reference="tbl-team" link="show">
                            <TextField source="nome" />
                        </ReferenceField>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">Note</Typography>
                        <Typography variant="body1"><TextField source="note" /></Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">Ordinamento</Typography>
                        <Typography variant="body1"><TextField source="ordinamento" /></Typography>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ width: '100%', mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Stati
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <BooleanField source="flAttivo" label="Attivo" />
                        <BooleanField source="flVisibile" label="Visibile" />
                        <BooleanField source="flAnnullato" label="Annullato" />
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ width: '100%' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                        Timestamp
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">Creato il</Typography>
                        <Typography variant="body2">
                            <DateField source="createdAt" showTime />
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="textSecondary">Aggiornato il</Typography>
                        <Typography variant="body2">
                            <DateField source="updatedAt" showTime />
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </SimpleShowLayout>
    </Show>
);
